"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.markupToHTML = exports.parseInside = exports.checkIfTextWithHtml = exports.checkIfClickedOnCanvas = exports.rgbToHex = exports.hexToString = exports.stringToHex = exports.colorValueToColorObject = exports.stringToColorNumber = exports.sanitizeColorValueToColorNumberAndAlpha = exports.getI18n = exports.getSaveAreaInsetTop = exports.getSaveAreaInsetBottom = exports.getSplittedValueObj = exports.getSplittedValue = exports.getValueByUnit = exports.getValueAndUnit = exports.getValueInRemUnit = exports.getValueInPxUnit = exports.getValueInVhUnit = exports.getValueInVwUnit = exports.setDevicePixelRatio = exports.getDevicePixelRatio = exports.specifiedDevicePixelRation = exports.arrayOfNumbersToStrings = exports.getTextureKeyFromArray = exports.isRGB = exports.isRGBA = exports.SAFE_AREA_INSET_TOP = exports.SAFE_AREA_INSET_BOTTOM = exports.HTML_BASE_FONT_SIZE = exports.CSS_EASING = exports.cubicBezier = exports.getCharacterDataPropsForRender = void 0;
const getCharacterDataPropsForRender = (character) => Object.assign({}, character.properties, { expression: character.expression });
exports.getCharacterDataPropsForRender = getCharacterDataPropsForRender;
/**
 * https://github.com/gre/bezier-easing
 * BezierEasing - use bezier curve for transition easing function
 * by Gaëtan Renaudeau 2014 - 2015 – MIT License
 */
exports.cubicBezier = (() => {
    // TODO: cleanup the code of this function a bit
    // These values are established by empiricism with tests (tradeoff: performance VS precision)
    const NEWTON_ITERATIONS = 4;
    const NEWTON_MIN_SLOPE = 0.001;
    const SUBDIVISION_PRECISION = 0.0000001;
    const SUBDIVISION_MAX_ITERATIONS = 10;
    const kSplineTableSize = 11;
    const kSampleStepSize = 1.0 / (kSplineTableSize - 1.0);
    const float32ArraySupported = typeof Float32Array === 'function';
    function aFunc(aA1, aA2) {
        return 1.0 - 3.0 * aA2 + 3.0 * aA1;
    }
    function bFunc(aA1, aA2) {
        return 3.0 * aA2 - 6.0 * aA1;
    }
    function cFunc(aA1) {
        return 3.0 * aA1;
    }
    // Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
    function calcBezier(aT, aA1, aA2) {
        return ((aFunc(aA1, aA2) * aT + bFunc(aA1, aA2)) * aT + cFunc(aA1)) * aT;
    }
    // Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
    function getSlope(aT, aA1, aA2) {
        return 3.0 * aFunc(aA1, aA2) * aT * aT + 2.0 * bFunc(aA1, aA2) * aT + cFunc(aA1);
    }
    function binarySubdivide(aX, aA, aB, mX1, mX2) {
        let currentX;
        let currentT;
        let i = 0;
        do {
            currentT = aA + (aB - aA) / 2.0;
            currentX = calcBezier(currentT, mX1, mX2) - aX;
            if (currentX > 0.0) {
                aB = currentT;
            }
            else {
                aA = currentT;
            }
        } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
        return currentT;
    }
    function newtonRaphsonIterate(aX, aGuessT, mX1, mX2) {
        for (let i = 0; i < NEWTON_ITERATIONS; ++i) {
            const currentSlope = getSlope(aGuessT, mX1, mX2);
            if (currentSlope === 0.0) {
                return aGuessT;
            }
            const currentX = calcBezier(aGuessT, mX1, mX2) - aX;
            aGuessT -= currentX / currentSlope;
        }
        return aGuessT;
    }
    function LinearEasing(x) {
        return x;
    }
    return function bezier(mX1, mY1, mX2, mY2) {
        if (!(mX1 >= 0 && mX1 <= 1 && mX2 >= 0 && mX2 <= 1)) {
            throw new Error('bezier x values must be in [0, 1] range');
        }
        if (mX1 === mY1 && mX2 === mY2) {
            return LinearEasing;
        }
        // Precompute samples table
        const sampleValues = float32ArraySupported ? new Float32Array(kSplineTableSize) : new Array(kSplineTableSize);
        for (let i = 0; i < kSplineTableSize; ++i) {
            sampleValues[i] = calcBezier(i * kSampleStepSize, mX1, mX2);
        }
        function getTForX(aX) {
            let intervalStart = 0.0;
            let currentSample = 1;
            const lastSample = kSplineTableSize - 1;
            for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
                intervalStart += kSampleStepSize;
            }
            --currentSample;
            // Interpolate to provide an initial guess for t
            const dist = (aX - sampleValues[currentSample]) /
                (sampleValues[currentSample + 1] - sampleValues[currentSample]);
            const guessForT = intervalStart + dist * kSampleStepSize;
            const initialSlope = getSlope(guessForT, mX1, mX2);
            if (initialSlope >= NEWTON_MIN_SLOPE) {
                return newtonRaphsonIterate(aX, guessForT, mX1, mX2);
            }
            else if (initialSlope === 0.0) {
                return guessForT;
            }
            return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize, mX1, mX2);
        }
        return function BezierEasing(x) {
            // Because JavaScript number are imprecise, we should guarantee the extremes are right.
            if (x === 0 || x === 1) {
                return x;
            }
            return calcBezier(getTForX(x), mY1, mY2);
        };
    };
})();
exports.CSS_EASING = {
    linear: (0, exports.cubicBezier)(0, 0, 1, 1),
    ease: (0, exports.cubicBezier)(0.25, 0.1, 0.25, 1),
    'ease-in-out': (0, exports.cubicBezier)(0.42, 0, 0.58, 1),
    'ease-in': (0, exports.cubicBezier)(0.42, 0, 1, 1),
    'ease-out': (0, exports.cubicBezier)(0, 0, 0.58, 1),
};
// TEXT BUBBLES AND CHOICES UTILS
exports.HTML_BASE_FONT_SIZE = 16; // 16px
exports.SAFE_AREA_INSET_BOTTOM = null;
exports.SAFE_AREA_INSET_TOP = null;
function isRGBA(rgba) {
    return rgba.indexOf("rgba") !== -1;
}
exports.isRGBA = isRGBA;
function isRGB(rgb) {
    return rgb.indexOf("rgb") !== -1;
}
exports.isRGB = isRGB;
function getTextureKeyFromArray(array) {
    return array.join('-').replace(/#/g, '');
}
exports.getTextureKeyFromArray = getTextureKeyFromArray;
function arrayOfNumbersToStrings(array) {
    return array.map(value => {
        const colorValue = colorValueToColorObject(value);
        return colorValue.rgba;
    });
}
exports.arrayOfNumbersToStrings = arrayOfNumbersToStrings;
// INFO: set as let so we can change it from GameplayRenderer if needed
exports.specifiedDevicePixelRation = window.devicePixelRatio || 1;
function getDevicePixelRatio() {
    return exports.specifiedDevicePixelRation;
}
exports.getDevicePixelRatio = getDevicePixelRatio;
function setDevicePixelRatio(dPR) {
    exports.specifiedDevicePixelRation = dPR;
}
exports.setDevicePixelRatio = setDevicePixelRatio;
function getValueInVwUnit(vwValue, canvasWidth) {
    if (!canvasWidth) {
        throw Error("[GameplayRenderer.getValueInVwUnit] missing canvasWidth");
    }
    return (vwValue / 100) * canvasWidth;
}
exports.getValueInVwUnit = getValueInVwUnit;
function getValueInVhUnit(vhValue, canvasHeight) {
    if (!canvasHeight) {
        throw Error("[GameplayRenderer.getValueInVhUnit] missing canvasHeight");
    }
    return (vhValue / 100) * canvasHeight;
}
exports.getValueInVhUnit = getValueInVhUnit;
function getValueInPxUnit(pxValue) {
    return pxValue * getDevicePixelRatio();
}
exports.getValueInPxUnit = getValueInPxUnit;
function getValueInRemUnit(remValue) {
    return getValueInPxUnit(exports.HTML_BASE_FONT_SIZE * remValue);
}
exports.getValueInRemUnit = getValueInRemUnit;
function getValueAndUnit(value) {
    const unitMatch = value.match(/([0-9.]+) *(px|vw|vh|fr)$/i);
    if (!unitMatch) {
        return null;
    }
    const [, finalValue, finalUnit] = unitMatch;
    return getSplittedValueObj(Number(finalValue), finalUnit);
}
exports.getValueAndUnit = getValueAndUnit;
/**
 * Get value calculated by provided unit
 * @returns number as pixel value
 */
function getValueByUnit(value, canvasWidth, canvasHeight) {
    if (!canvasWidth) {
        throw Error("[GameplayRenderer.getValueByUnit] missing canvasWidth");
    }
    if (!canvasHeight) {
        throw Error("[GameplayRenderer.getValueByUnit] missing canvasHeight");
    }
    if (value === undefined || value === null) {
        return null;
    }
    // if number - set as pixel value
    if (typeof value === 'number') {
        return getSplittedValueObj(getValueInPxUnit(value), 'px');
    }
    // check what is the unit
    const unitMatch = getValueAndUnit(value); // value.match(/([0-9.]+) *(px|vw|vh|fr)$/i);
    if (!unitMatch) {
        return null;
    }
    const finalValue = unitMatch.value;
    const finalUnit = unitMatch.unit;
    let toReturnValue = 1;
    let toReturnUnit = 'px';
    if (finalUnit === 'vw') {
        toReturnValue = getValueInVwUnit(Number(finalValue), canvasWidth);
    }
    else if (finalUnit === 'vh') {
        toReturnValue = getValueInVhUnit(Number(finalValue), canvasHeight);
    }
    else if (finalUnit === 'fr') {
        toReturnValue = Number(finalValue);
        toReturnUnit = finalUnit;
    }
    else {
        toReturnValue = getValueInPxUnit(Number(finalValue));
    }
    return getSplittedValueObj(toReturnValue, toReturnUnit);
}
exports.getValueByUnit = getValueByUnit;
/**
 * Get value splitted for two values
 * @returns  array with number as pixel value
 */
function getSplittedValue(value, canvasWidth, canvasHeight) {
    if (!canvasWidth) {
        throw Error("[GameplayRenderer.getSplittedValue] missing canvasWidth");
    }
    if (!canvasHeight) {
        throw Error("[GameplayRenderer.getSplittedValue] missing canvasHeight");
    }
    if (value === undefined || value === null) {
        return null;
    }
    if (typeof value === 'string') {
        const strippedValue = value.trim().replace(';', '');
        const splitValues = strippedValue.split(' ');
        const arrayToReturn = [];
        for (let i = 0; i < splitValues.length; i++) {
            const valueToUse = getValueByUnit(splitValues[i], canvasWidth, canvasHeight);
            if (valueToUse) {
                arrayToReturn.push(valueToUse);
            }
        }
        return arrayToReturn;
    }
    else if (typeof value === 'number') {
        const vertical = getSplittedValueObj(value, 'px');
        const horizontal = getSplittedValueObj(value, 'px');
        return [vertical, horizontal];
    }
    return null;
}
exports.getSplittedValue = getSplittedValue;
function getSplittedValueObj(value, unit) {
    return { value, unit };
}
exports.getSplittedValueObj = getSplittedValueObj;
function getSaveAreaInsetBottom() {
    if (exports.SAFE_AREA_INSET_BOTTOM !== null) {
        return exports.SAFE_AREA_INSET_BOTTOM;
    }
    const div = document.createElement("div");
    div.style.bottom = 'env(safe-area-inset-bottom, 0px)';
    document.body.appendChild(div);
    exports.SAFE_AREA_INSET_BOTTOM = getComputedStyle(div).getPropertyValue('bottom') || '0px';
    document.body.removeChild(div);
    return exports.SAFE_AREA_INSET_BOTTOM;
}
exports.getSaveAreaInsetBottom = getSaveAreaInsetBottom;
function getSaveAreaInsetTop() {
    if (exports.SAFE_AREA_INSET_TOP !== null) {
        return exports.SAFE_AREA_INSET_TOP;
    }
    const div = document.createElement("div");
    div.style.top = 'env(safe-area-inset-top, 0px)';
    document.body.appendChild(div);
    exports.SAFE_AREA_INSET_TOP = getComputedStyle(div).getPropertyValue('top') || '0px';
    document.body.removeChild(div);
    return exports.SAFE_AREA_INSET_TOP;
}
exports.getSaveAreaInsetTop = getSaveAreaInsetTop;
/**
 * FIXME: for future use if need to make i18n
 */
function getI18n() {
    return "en-US";
}
exports.getI18n = getI18n;
function sanitizeColorValueToColorNumberAndAlpha(color) {
    let toReturn = { hexNum: 0, alpha: 1 };
    const colorObj = colorValueToColorObject(color);
    toReturn.hexNum = colorObj.color;
    toReturn.alpha = colorObj.alpha;
    return toReturn;
}
exports.sanitizeColorValueToColorNumberAndAlpha = sanitizeColorValueToColorNumberAndAlpha;
/**
 * @param str ex: #000000
 */
function stringToColorNumber(str) {
    const colorObj = colorValueToColorObject(str);
    return { colorNum: colorObj.color, colorAlpha: colorObj.alpha };
}
exports.stringToColorNumber = stringToColorNumber;
function colorValueToColorObject(colorValue) {
    return Phaser.Display.Color.ValueToColor(colorValue);
}
exports.colorValueToColorObject = colorValueToColorObject;
/**
 * @param str ex: #00
 */
function stringToHex(str) {
    return parseInt(str.replace(/^#/, ''), 16);
}
exports.stringToHex = stringToHex;
/**
 * @param number ex: 0x000000
 */
function hexToString(number) {
    const colorsStr = number.toString();
    const redStr = colorsStr.slice(0, 2);
    const greenStr = colorsStr.slice(2, 4);
    const blueStr = colorsStr.slice(4, 6);
    return `#${redStr}${greenStr}${blueStr}`;
}
exports.hexToString = hexToString;
/**
 * @param rgb rgb(2,2,2) or rgba(2,2,2,1);
 */
function rgbToHex(rgb) {
    const isRGBABool = isRGBA(rgb);
    let rgbToCheck = rgb.substring(isRGBABool ? 5 : 4).split(")")[0];
    let data = rgbToCheck.split(',');
    for (let i = 0; i < 3; i++) {
        data[i] = parseInt(data[i]).toString(16);
        if (data[i].length === 1) {
            data[i] = "0" + data[i];
        }
    }
    let [red, green, blue, alpha] = data;
    // let alpha: number | string = data[3];
    let alphaNum = 1;
    if (alpha !== undefined && alpha !== null) {
        alphaNum = parseInt(alpha) / 255;
    }
    const hexString = `#${red}${green}${blue}`;
    return { hexNum: hexString, alpha: alphaNum };
}
exports.rgbToHex = rgbToHex;
function checkIfClickedOnCanvas(downElement, isCheckEnabled = true) {
    // FIXME: additional option to disable/enable it if needed
    if (!isCheckEnabled) {
        return true;
    }
    if (downElement !== null && downElement !== undefined) {
        const canClick = (downElement instanceof HTMLCanvasElement);
        // FIXME: in creation tools it returns something else
        return canClick;
    }
    // return true by default so it will go ahead when not clickedElement specified
    return true;
}
exports.checkIfClickedOnCanvas = checkIfClickedOnCanvas;
function checkIfTextWithHtml(text) {
    return /<[a-z/][\s\S]*>/i.test(text);
}
exports.checkIfTextWithHtml = checkIfTextWithHtml;
/**
 * Copy from: authoring-tool\web\src\components\pages\StoryPreview\textPreview.js
 * @param txt
 * @param left
 * @param right
 * @returns
 */
function parseInside(txt, left, right) {
    return left + txt + right;
}
exports.parseInside = parseInside;
function markupToHTML(text) {
    if (!text) {
        return text;
    }
    text = text.replace(/_([^\s_][\s\S]*?)_/g, function (wm, m) {
        return (/\S$/.test(m)) ? parseInside(m, '<i>', '</i>') : wm;
    });
    text = text.replace(/\*([^\s*][\s\S]*?)\*/g, function (wm, m) {
        return (/\S$/.test(m)) ? parseInside(m, '<strong>', '</strong>') : wm;
    });
    // MICHAL: disable below as we don't need span style - we use c=color directly in RichTextField
    // text = text.replace(/<c=([^>]*)>([^\s*][\s\S]*?)<\/c>/g, function (wm, m1, m2) {
    //   const regexp = /(#([\da-fA-F]{3}){1,2}|(rgb|hsl)a\((\d{1,3}%?,\s?){3}(1|0?\.\d+)\)|(rgb|hsl)\(\d{1,3}%?(,\s?\d{1,3}%?){2}\)|[A-Za-z]+)/g;
    //   const found = m1.match(regexp);
    //   if (found) {
    //     return (/\S$/.test(m2)) ? parseInside(m2, `<span style="color:${found[0]};">`, '</span>') : wm;
    //   }
    //   return wm;
    // });
    return text;
}
exports.markupToHTML = markupToHTML;
